/**
 * The facts a pack stands on, and the one a person has to supply.
 *
 * Every other fact is measured: the pitch has a default and a cleanup flag,
 * the clock and the attacking direction come out of the tracking. Team
 * identity is different — no amount of tracking tells you which club is
 * called what — so this is the only fact with an editor, and it is here
 * because "fact team_identity — source: none" is read three lines above it.
 */
import { useCallback, useEffect, useState } from 'react'

import { getGameFacts, updateGameFacts, type GameFactsPayload } from '../packs/api'

const label = (v: string | null | undefined) => v || '—'

/** A fact's provenance, in the words the vocabulary uses.
 *
 *  'declared' is a person saying so and must never read as 'measured'. */
export function sourceNote(source: string | null, assumed?: boolean): string {
  if (assumed) return 'platform default — not this venue'
  if (!source) return 'not recorded'
  if (source === 'declared') return 'entered by a person'
  if (source === 'legacy') return 'provenance unknown'
  return source
}

export default function GameFactsEditor({ gameCode, onSaved }: {
  gameCode: string
  onSaved?: () => void
}) {
  const [data, setData] = useState<GameFactsPayload | null>(null)
  const [home, setHome] = useState('')
  const [away, setAway] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const load = useCallback(() => {
    getGameFacts(gameCode)
      .then((d) => {
        setData(d)
        // Pre-filled from the recorded value, or from what the game code
        // implies. The suggestion is a starting point a person confirms —
        // AWAY@HOME is a convention, and a convention that is wrong once
        // would mislabel a game's ends for good.
        setHome(d.facts.identity.home_team_name ?? d.suggested.home_team_name ?? '')
        setAway(d.facts.identity.away_team_name ?? d.suggested.away_team_name ?? '')
      })
      .catch(() => setError('Could not load this game’s facts.'))
  }, [gameCode])

  useEffect(load, [load])

  const save = () => {
    setBusy(true); setError(null); setSaved(false)
    updateGameFacts(gameCode, {
      home_team_name: home.trim(),
      away_team_name: away.trim(),
      reason: `declared team identity for ${gameCode}`,
    })
      .then((d) => {
        setData(d)
        setSaved(true)
        onSaved?.()
      })
      .catch((e: unknown) => setError(
        (e as { message?: string }).message || 'Could not save.'))
      .finally(() => setBusy(false))
  }

  if (!data) {
    return <p className="muted">{error ?? 'Loading game facts…'}</p>
  }

  const f = data.facts
  const complete = Boolean(f.identity.home_team_name && f.identity.away_team_name)

  return (
    <div className="game-facts">
      <h3>Game facts</h3>

      <dl className="game-facts__read">
        <dt>Pitch</dt>
        <dd>
          {f.pitch.length_cm && f.pitch.width_cm
            ? `${(f.pitch.length_cm / 100).toFixed(1)} × ${(f.pitch.width_cm / 100).toFixed(1)} m`
            : '—'}
          <span className="muted"> · {sourceNote(f.pitch.source, f.pitch.assumed)}</span>
        </dd>
        <dt>Frame rate</dt>
        <dd>{f.timing.fps ?? '—'}<span className="muted"> · {sourceNote(f.timing.source)}</span></dd>
        <dt>Half time</dt>
        <dd>{f.timing.half2_frame ?? '—'}<span className="muted"> · {sourceNote(f.timing.source)}</span></dd>
        <dt>First-half direction</dt>
        <dd>{label(f.direction.home_attack_dir_h1)}<span className="muted"> · {sourceNote(f.direction.source)}</span></dd>
      </dl>

      <form className="game-facts__form" onSubmit={(e) => { e.preventDefault(); save() }}>
        <p className="game-facts__lead">
          Team identity{' '}
          <span className="muted">· {sourceNote(f.identity.source)}</span>
        </p>
        {!complete && (
          <p className="game-facts__todo">
            Packs that need <span className="mono">team_identity</span> will not
            run until both names are set.
          </p>
        )}
        <label>Home team
          <input type="text" value={home} placeholder="e.g. University of Florida"
                 onChange={(e) => setHome(e.target.value)} />
        </label>
        <label>Away team
          <input type="text" value={away} placeholder="e.g. UCF Women"
                 onChange={(e) => setAway(e.target.value)} />
        </label>
        <p className="muted game-facts__hint">
          The game code reads <span className="mono">{data.suggested.away_team_name}</span>
          {' '}at <span className="mono">{data.suggested.home_team_name}</span> —
          away first. Check it before saving; the code is a naming convention,
          not a record.
        </p>
        <div className="game-facts__actions">
          <button type="submit" disabled={busy || !home.trim() || !away.trim()}>
            {busy ? 'Saving…' : 'Save identity'}
          </button>
          {saved && <span className="game-facts__saved">Saved</span>}
        </div>
        {error && <p className="lab-error">{error}</p>}
      </form>
    </div>
  )
}
